import { hasDefinedValue, isEmpty } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { LearnMore } from 'components/shared/LearnMore/LearnMore';
import { ProductCategoryItem } from 'components/shared/ProductAndClientSelector/ProductCategoryItem';
import { ProductQuickStartButton } from 'components/shared/ProductAndClientSelector/ProductQuickStartButton';
import type { IntlKey } from 'locales';
import { sortBy } from 'lodash-es';
import type { ProductVariation, SelectedProductVariation } from 'models/productsReferential';
import { CSSProperties, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AppState } from 'state/state.model';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { getActivatedAssetTypeFilter } from 'state/workspaces/workspaces/workspaces.selector';

import { ProductPinnedButton } from './ProductPinnedButton';
export interface ProductWrapperSelectorProps {
  className?: string;
  style?: CSSProperties;
  workspace: WorkspaceWithProductSelector;
  availableProductVariations: readonly ProductVariation[];
  selectedProductVariation: Partial<SelectedProductVariation>;
  selectedProductType?: string;
  selectedProductSubType?: string;
  selectedProductWrapper?: string;
  selectedPayoffType?: string;
  onProductWrapperSelected(wrapper: string): void;
  onProductVariationQuickStartSelected(selectedProductVariation: SelectedProductVariation): void;
}

const isTypeDefined = hasDefinedValue('productType');
const isSubtypeDefined = hasDefinedValue('productSubType');
const isRequestTypeDefined = hasDefinedValue('requestType');

export function ProductWrapperSelector({
  className,
  style,
  workspace,
  availableProductVariations,
  selectedProductVariation,
  onProductWrapperSelected,
  onProductVariationQuickStartSelected,
}: ProductWrapperSelectorProps) {
  const { formatMessage } = useIntl();
  const activatedAssetTypeFilter = useSelector((state: AppState) => getActivatedAssetTypeFilter(state, workspace));

  const wrapperOptions = useGetWrapperOptions(
    availableProductVariations,
    activatedAssetTypeFilter,
    selectedProductVariation.productType,
    selectedProductVariation.productSubType,
  );

  return (
    <div
      data-e2e="product-wrapper-selector"
      className={classNames('d-flex flex-column overflow-auto', className)}
      style={style}
    >
      {isEmpty(wrapperOptions) ? (
        <NoProductWrappers />
      ) : (
        <>
          {wrapperOptions.map(({ label, value, isStatified }) => {
            const productVariation = {
              ...selectedProductVariation,
              wrapper: value,
            };
            return (
              <ProductCategoryItem
                key={value}
                className="ps-3 pe-0"
                e2e="product-wrapper-selector-item"
                isItemSelected={selectedProductVariation.wrapper === value}
                onItemClick={() => onProductWrapperSelected(value)}
                onItemDoubleClick={() => {
                  if (
                    !isTypeDefined(productVariation) ||
                    !isSubtypeDefined(productVariation) ||
                    !isRequestTypeDefined(productVariation)
                  ) {
                    return;
                  }
                  onProductVariationQuickStartSelected(productVariation);
                }}
              >
                <span className="text-wrap text-start">{label}</span>
                {isTypeDefined(productVariation) &&
                  isSubtypeDefined(productVariation) &&
                  isRequestTypeDefined(productVariation) && (
                    <div className="d-flex justify-content-end pe-2">
                      {isStatified && (
                        <Tooltip
                          position="right"
                          contents={formatMessage({ id: 'productSelector.dynamic.isStatified' })}
                        >
                          <em className="icon icon-sm icon-outlined me-2 ">playlist_add_check</em>
                        </Tooltip>
                      )}
                      <ProductPinnedButton
                        className="text-muted"
                        size="sm"
                        productVariation={productVariation}
                        workspace={workspace}
                        tabType="catalogue"
                      />
                      <ProductQuickStartButton
                        onClick={() => onProductVariationQuickStartSelected(productVariation)}
                        workspace={workspace}
                        tabType="catalogue"
                        selectedProductVariation={productVariation}
                        className="ms-2"
                      />
                    </div>
                  )}
              </ProductCategoryItem>
            );
          })}
          {workspace === 'static' && (
            <div className="justify-content-end mx-3 mt-auto mb-3">
              <LearnMore
                mini
                productType={selectedProductVariation.productType}
                productSubType={selectedProductVariation.productSubType}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NoProductWrappers() {
  return (
    <div className="h-100 d-flex flex-column justify-content-center align-items-center px-4">
      <span className="icon icon-outlined icon-lg text-secondary">group_work</span>
      <p className="text-center text-secondary">
        <FormattedMessage id="productSelector.available.wrappers" />
      </p>
    </div>
  );
}

const useGetWrapperOptions = (
  productVariations: readonly ProductVariation[],
  activatedAssetTypeFilter: string,
  productType?: string,
  productSubType?: string,
) => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    if (productType === undefined || productSubType === undefined) {
      return [];
    }

    const filteredWrappers = productVariations
      .filter(v => v.productType === productType && v.productSubType === productSubType)
      .filter(
        ({ assetTypes }) =>
          activatedAssetTypeFilter === 'All' || assetTypes?.some(assetType => activatedAssetTypeFilter === assetType),
      );

    const distinctWrapperOptions = filteredWrappers.map(wrappers => ({
      label: formatMessage({ id: `quote.products.${wrappers.wrapper}` as IntlKey }),
      value: wrappers.wrapper,
      isStatified: wrappers.isStatified,
    }));

    return sortBy(distinctWrapperOptions, o => o.label);
  }, [activatedAssetTypeFilter, formatMessage, productSubType, productType, productVariations]);
};
